import { useEffect, useState } from 'react'
import { Boxes, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'
import { formatAddressFields } from '@/features/associated-parties/formatAddress'
import { ComplianceSubPageHeader } from '@/features/compliance-studio/machinery-directive/components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '@/features/compliance-studio/machinery-directive/components/ComplianceSubPageTitle'
import {
  useCurrentOrganization,
  type Organization,
} from '@/shared/hooks/api/organization'
import { useGetProduct } from '@/shared/hooks/api/product'
import {
  useGetCraDeclarationOfConformity,
  useGetCraStudio,
} from './api'

const DOWNLOAD_DELAY_MS = 3000

const VULNERABILITY_POLICY_PDF_PATH =
  '/cra/vulnerability-policy-2026-10-20-deutsch.pdf'
const VULNERABILITY_POLICY_PDF_FILENAME =
  'vulnerability-policy-2026-10-20-deutsch.pdf'

const TECHNICAL_DOCUMENTATION_PDF_PATH =
  '/cra/Technical_Documentation_Deutsch_XHQ630_Vertical_5-Axis_Machining_Center.pdf'
const TECHNICAL_DOCUMENTATION_PDF_FILENAME =
  'Technical_Documentation_Deutsch_XHQ630_Vertical_5-Axis_Machining_Center.pdf'

function downloadStaticPdf(path: string, filename: string) {
  const link = document.createElement('a')
  link.href = path
  link.download = filename
  link.click()
}

function DelayedDownloadButton({
  path,
  filename,
  label,
  variant = 'default',
}: {
  path: string
  filename: string
  label: string
  variant?: 'default' | 'outline'
}) {
  const [pending, setPending] = useState(false)

  useEffect(() => {
    if (!pending) {
      return
    }
    const timer = window.setTimeout(() => {
      downloadStaticPdf(path, filename)
      setPending(false)
    }, DOWNLOAD_DELAY_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [pending, path, filename])

  return (
    <Button
      type='button'
      variant={variant}
      disabled={pending}
      onClick={() => {
        setPending(true)
      }}
    >
      {pending ? <Loader2 className='size-4 animate-spin' /> : null}
      {label}
    </Button>
  )
}

function formatManufacturerAddress(
  organization: Organization | undefined,
  getCountryName: (countryCode: string) => string
): string {
  if (!organization) {
    return ''
  }
  return formatAddressFields(
    {
      street: organization.street,
      streetNumber: organization.streetNumber,
      street2: organization.street2,
      postCode: organization.postCode,
      city: organization.city,
      subDivision: organization.subDivision,
      country: organization.country,
      poBox: organization.poBox,
    },
    {
      getCountryName,
      translate: (key, options) => {
        if (key === 'emptyValue') {
          return ''
        }
        const value = options?.value
        if (typeof value === 'string') {
          return value
        }
        return ''
      },
    }
  )
}

function ReadOnlyControl({
  id,
  value,
  placeholder,
  multiline,
}: {
  id: string
  value: string
  placeholder?: string
  multiline?: boolean
}) {
  if (multiline) {
    return (
      <Textarea
        id={id}
        readOnly
        value={value}
        placeholder={placeholder}
        className='min-h-20'
      />
    )
  }
  return (
    <Input id={id} readOnly value={value} placeholder={placeholder} />
  )
}

function FieldControl({
  id,
  value,
  placeholder,
  multiline,
  action,
}: {
  id: string
  value: string
  placeholder?: string
  multiline?: boolean
  action?: string
}) {
  if (action) {
    return (
      <div className='flex items-start gap-3'>
        <div className='min-w-0 flex-1'>
          <ReadOnlyControl
            id={id}
            value={value}
            placeholder={placeholder}
            multiline={multiline}
          />
        </div>
        <Button type='button' size='sm' variant='outline'>
          {action}
        </Button>
      </div>
    )
  }
  return (
    <ReadOnlyControl
      id={id}
      value={value}
      placeholder={placeholder}
      multiline={multiline}
    />
  )
}

function ReadOnlyField({
  id,
  label,
  value,
  placeholder,
  multiline,
  action,
}: {
  id: string
  label: string
  value: string
  placeholder?: string
  multiline?: boolean
  action?: string
}) {
  return (
    <div className='grid gap-2 sm:grid-cols-[12rem_1fr] sm:items-start'>
      <Label htmlFor={id} className='pt-2 font-normal text-muted-foreground'>
        {label}
      </Label>
      <FieldControl
        id={id}
        value={value}
        placeholder={placeholder}
        multiline={multiline}
        action={action}
      />
    </div>
  )
}

function AttachRow({
  title,
  status,
  action,
}: {
  title: string
  status: string
  action: string
}) {
  return (
    <div className='flex items-center justify-between gap-3 rounded-md border px-3 py-2'>
      <div>
        <p className='text-sm font-medium'>{title}</p>
        <p className='text-xs text-muted-foreground'>{status}</p>
      </div>
      <Button type='button' size='sm' variant='outline'>
        {action}
      </Button>
    </div>
  )
}

function LinkedFileRow({
  title,
  completed,
}: {
  title: string
  completed: boolean
}) {
  const { t } = useTranslation('craCompliance')
  let detail = t('technicalDocumentation.notStartedDetail')
  let status = t('technicalDocumentation.notStarted')
  let variant: 'secondary' | 'success' = 'secondary'
  if (completed) {
    detail = t('technicalDocumentation.completedDetail')
    status = t('technicalDocumentation.completed')
    variant = 'success'
  }
  return (
    <div className='flex items-center justify-between gap-3 border-b py-3 last:border-b-0'>
      <div>
        <p className='text-sm font-medium'>{title}</p>
        <p className='text-xs text-muted-foreground'>{detail}</p>
      </div>
      <Badge variant={variant}>{status}</Badge>
    </div>
  )
}

export function CraTechnicalDocumentationPage() {
  const { t } = useTranslation('craCompliance')
  const { t: tCountry } = useTranslation('countries')
  const { productId = '', craStudioId = '' } = useParams()
  const { data: organization } = useCurrentOrganization()
  const { data: product } = useGetProduct(productId)
  const { data: studio } = useGetCraStudio(craStudioId)
  const { data: declaration } = useGetCraDeclarationOfConformity(craStudioId)

  const address = formatManufacturerAddress(organization, (code) =>
    tCountry(code.toLowerCase(), { defaultValue: code })
  )
  const subtitleParts = [t('technicalDocumentation.annexLabel')]
  if (product?.name) {
    subtitleParts.push(product.name)
  }
  if (product?.sku) {
    subtitleParts.push(product.sku)
  }
  const productName = product?.name?.trim() || t('technicalDocumentation.thisProduct')
  const riskAssessmentCompleted =
    Boolean(studio?.generatedAt) || (studio?.sections?.length ?? 0) > 0
  const declarationCompleted = Boolean(declaration?.markdown?.trim())

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-y-auto p-4'>
      <ComplianceSubPageHeader>
        <div>
          <ComplianceSubPageTitle>
            {t('nav.technicalDocumentation')}
          </ComplianceSubPageTitle>
          <p className='text-sm text-muted-foreground'>
            {subtitleParts.join(' · ')}
          </p>
        </div>
        <DelayedDownloadButton
          path={TECHNICAL_DOCUMENTATION_PDF_PATH}
          filename={TECHNICAL_DOCUMENTATION_PDF_FILENAME}
          label={t('technicalDocumentation.export')}
        />
      </ComplianceSubPageHeader>

      <div className='flex flex-col gap-4'>
        <Card>
          <CardHeader>
            <CardTitle>{t('technicalDocumentation.manufacturerTitle')}</CardTitle>
            <CardDescription>
              {t('technicalDocumentation.manufacturerDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col gap-4'>
            <ReadOnlyField
              id='cra-tech-manufacturer-name'
              label={t('technicalDocumentation.name')}
              value={organization?.name ?? ''}
            />
            <ReadOnlyField
              id='cra-tech-manufacturer-address'
              label={t('technicalDocumentation.address')}
              value={address}
            />
            <ReadOnlyField
              id='cra-tech-manufacturer-email'
              label={t('technicalDocumentation.email')}
              value={organization?.contactEmail ?? ''}
            />
            <ReadOnlyField
              id='cra-tech-manufacturer-contact'
              label={t('technicalDocumentation.contactPerson')}
              value={organization?.contactName ?? ''}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('technicalDocumentation.productTitle')}</CardTitle>
            <CardDescription>
              {t('technicalDocumentation.productDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col gap-4'>
            <ReadOnlyField
              id='cra-tech-product-name'
              label={t('technicalDocumentation.name')}
              value={product?.name ?? ''}
            />
            <ReadOnlyField
              id='cra-tech-product-type'
              label={t('technicalDocumentation.type')}
              value=''
            />
            <ReadOnlyField
              id='cra-tech-product-model'
              label={t('technicalDocumentation.model')}
              value={product?.productModel ?? ''}
            />
            <ReadOnlyField
              id='cra-tech-product-sku'
              label={t('technicalDocumentation.sku')}
              value={product?.sku ?? ''}
            />
            <ReadOnlyField
              id='cra-tech-product-description'
              label={t('technicalDocumentation.description')}
              value={product?.description ?? ''}
              placeholder={t('technicalDocumentation.notSavedYet')}
              action={t('technicalDocumentation.generate')}
              multiline
            />
            <ReadOnlyField
              id='cra-tech-intended-use'
              label={t('technicalDocumentation.intendedUse')}
              value=''
              placeholder={t('technicalDocumentation.notSavedYet')}
              action={t('technicalDocumentation.generate')}
              multiline
            />
            <ReadOnlyField
              id='cra-tech-lifetime'
              label={t('technicalDocumentation.expectedLifetime')}
              value=''
              placeholder={t('technicalDocumentation.enterOnThisPage')}
            />
            <ReadOnlyField
              id='cra-tech-support-ends'
              label={t('technicalDocumentation.supportPeriodEnds')}
              value=''
              placeholder={t('technicalDocumentation.enterMonthAndYear')}
            />
            <ReadOnlyField
              id='cra-tech-support-reason'
              label={t('technicalDocumentation.supportPeriodReason')}
              value=''
              placeholder={t('technicalDocumentation.enterSupportReason')}
              multiline
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('technicalDocumentation.softwareTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Empty className='border border-dashed'>
              <EmptyHeader>
                <EmptyMedia variant='icon'>
                  <Boxes />
                </EmptyMedia>
                <EmptyTitle>
                  {t('technicalDocumentation.softwareEmptyTitle')}
                </EmptyTitle>
                <EmptyDescription>
                  {t('technicalDocumentation.softwareDescription')}
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button type='button' size='sm' variant='outline'>
                  {t('technicalDocumentation.addRow')}
                </Button>
              </EmptyContent>
            </Empty>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className='flex flex-wrap items-center gap-2'>
              <CardTitle>{t('technicalDocumentation.standardsTitle')}</CardTitle>
              <Badge
                variant='outline'
                className='border-violet-200 bg-violet-50 font-normal text-violet-800 dark:border-violet-800/50 dark:bg-violet-950/40 dark:text-violet-200'
              >
                {t('technicalDocumentation.comingSoon')}
              </Badge>
            </div>
            <CardDescription>
              {t('technicalDocumentation.standardsDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col gap-4'>
            <RadioGroup value='none'>
              <div className='flex items-center gap-2'>
                <RadioGroupItem
                  value='none'
                  id='cra-tech-no-standards'
                />
                <Label htmlFor='cra-tech-no-standards' className='font-normal'>
                  {t('technicalDocumentation.noStandardsApplied')}
                </Label>
              </div>
            </RadioGroup>
            <Alert variant='warning'>
              <AlertDescription>
                {t('technicalDocumentation.noHarmonisedStandards')}
              </AlertDescription>
            </Alert>
            <ReadOnlyField
              id='cra-tech-solutions'
              label={t('technicalDocumentation.solutionsLabel')}
              value=''
              placeholder={t('technicalDocumentation.enterSolutions')}
              multiline
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('technicalDocumentation.descriptionsTitle')}</CardTitle>
            <CardAction>
              <DelayedDownloadButton
                path={VULNERABILITY_POLICY_PDF_PATH}
                filename={VULNERABILITY_POLICY_PDF_FILENAME}
                label={t('technicalDocumentation.generateDraft')}
              />
            </CardAction>
          </CardHeader>
          <CardContent className='flex flex-col gap-6'>
            <div>
              <p className='text-sm font-medium'>
                {t('technicalDocumentation.vulnerabilityHandling')}
              </p>
              <p className='text-sm text-muted-foreground'>
                {t('technicalDocumentation.vulnerabilityCoach', {
                  productName,
                })}
              </p>
            </div>
            <div>
              <p className='text-sm font-medium'>
                {t('technicalDocumentation.productionMonitoring')}
              </p>
              <p className='text-sm text-muted-foreground'>
                {t('technicalDocumentation.productionCoach', {
                  productName,
                })}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('technicalDocumentation.annexITitle')}</CardTitle>
            <CardDescription>
              {t('technicalDocumentation.annexIDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col gap-3'>
            <AttachRow
              title={t('technicalDocumentation.drawing')}
              status={t('technicalDocumentation.notAttached')}
              action={t('technicalDocumentation.attach')}
            />
            <AttachRow
              title={t('technicalDocumentation.softwareBillOfMaterials')}
              status={t('technicalDocumentation.notAttached')}
              action={t('technicalDocumentation.attach')}
            />
            <div className='flex flex-col gap-3 pt-2'>
              <p className='text-sm font-medium'>
                {t('technicalDocumentation.photographs')}
              </p>
              <RadioGroup value='hardware'>
                <div className='flex items-start gap-2'>
                  <RadioGroupItem
                    value='hardware'
                    id='cra-tech-includes-hardware'
                  />
                  <Label
                    htmlFor='cra-tech-includes-hardware'
                    className='font-normal'
                  >
                    {t('technicalDocumentation.includesHardware')}
                  </Label>
                </div>
                <div className='flex items-start gap-2'>
                  <RadioGroupItem
                    value='software'
                    id='cra-tech-software-only'
                  />
                  <Label
                    htmlFor='cra-tech-software-only'
                    className='font-normal'
                  >
                    {t('technicalDocumentation.softwareOnly')}
                  </Label>
                </div>
              </RadioGroup>
              <AttachRow
                title={t('technicalDocumentation.externalFeatures')}
                status={t('technicalDocumentation.notAttached')}
                action={t('technicalDocumentation.attach')}
              />
              <AttachRow
                title={t('technicalDocumentation.marking')}
                status={t('technicalDocumentation.notAttached')}
                action={t('technicalDocumentation.attach')}
              />
              <AttachRow
                title={t('technicalDocumentation.internalLayout')}
                status={t('technicalDocumentation.notAttached')}
                action={t('technicalDocumentation.attach')}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-start justify-between gap-3'>
            <CardTitle>{t('technicalDocumentation.annexIITitle')}</CardTitle>
            <Button type='button' variant='outline'>
              {t('technicalDocumentation.addTestReport')}
            </Button>
          </CardHeader>
          <CardContent className='flex flex-col gap-4'>
            <div>
              <p className='text-sm font-medium'>
                {t('technicalDocumentation.noTestReports')}
              </p>
              <p className='text-xs text-muted-foreground'>
                {t('technicalDocumentation.noTestReportsHint')}
              </p>
            </div>
            <RadioGroup value='none'>
              <div className='flex items-center gap-2'>
                <RadioGroupItem
                  value='none'
                  id='cra-tech-no-test-reports'
                />
                <Label htmlFor='cra-tech-no-test-reports' className='font-normal'>
                  {t('technicalDocumentation.noTestReportsChoice')}
                </Label>
              </div>
            </RadioGroup>
            <Alert variant='warning'>
              <AlertDescription>
                {t('technicalDocumentation.nonComplianceWarning')}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('technicalDocumentation.linkedTitle')}</CardTitle>
            <CardDescription>
              {t('technicalDocumentation.linkedDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col gap-4'>
            <div>
              <LinkedFileRow
                title={t('nav.riskAssessment')}
                completed={riskAssessmentCompleted}
              />
              <LinkedFileRow
                title={t('nav.userInformation')}
                completed
              />
              <LinkedFileRow
                title={t('nav.declarationOfConformity')}
                completed={declarationCompleted}
              />
            </div>
            <Alert variant='warning'>
              <AlertDescription>
                {t('technicalDocumentation.exportWaiting')}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
